import type { User } from "firebase/auth";

export type PlanReminderPaymentMethod = "ves" | "stripe" | "binance";

export interface PlanRemindersLastRunSummary {
  ranAt?: string;
  dryRun?: boolean;
  scanned?: number;
  matched?: number;
  sent?: number;
  skippedAlreadySent?: number;
  skippedNoEmail?: number;
  errors?: number;
}

export interface PlanRemindersConfig {
  daysBeforeRenewal: number;
  remindOnExpiration: boolean;
  remindMidGrace: boolean;
  midGraceDay: number;
  remindGraceEnd: boolean;
  atRiskDaysAfterGrace: number;
  vesGraceDays: number;
  includeTrialing: boolean;
  paymentMethods: PlanReminderPaymentMethod[];
  timeZone: string;
  scheduleHour: number;
  scheduleMinute: number;
  schedulerEnabled: boolean;
  schedulerJobId: string | null;
  schedulerJobName: string | null;
  schedulerLocation: string | null;
  schedulerSchedule: string | null;
  schedulerTargetBaseUrl: string | null;
  lastRunAt: string | null;
  lastRunSummary: PlanRemindersLastRunSummary | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface PlanRemindersRunResult {
  ranAt: string;
  dryRun: boolean;
  scanned: number;
  matched: number;
  sent: number;
  skippedAlreadySent: number;
  skippedNoEmail: number;
  errors: number;
  details?: Array<Record<string, unknown>>;
}

export interface AtRiskOrganization {
  id: string;
  name: string;
  email: string | null;
  planName: string;
  paymentMethod: string;
  subscriptionStatus: string;
  currentPeriodEnd: string;
  graceEndedDaysAgo: number;
  vesGraceDays: number;
}

export interface AtRiskOrganizationsResponse {
  generatedAt: string;
  timeZone: string;
  vesGraceDays: number;
  atRiskDaysAfterGrace: number;
  paymentMethods: PlanReminderPaymentMethod[];
  count: number;
  organizations: AtRiskOrganization[];
}

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

const parseError = async (response: Response): Promise<string> => {
  const data = (await response.json().catch(() => null)) as unknown;
  if (data && typeof data === "object" && "detail" in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      return String((detail as { message: unknown }).message);
    }
  }
  return `Backend error (${response.status}).`;
};

const asMethod = (value: unknown): PlanReminderPaymentMethod | null => {
  if (value === "ves" || value === "stripe" || value === "binance") {
    return value;
  }
  return null;
};

const mapConfig = (data: Record<string, unknown>): PlanRemindersConfig => {
  const methods = Array.isArray(data.paymentMethods)
    ? data.paymentMethods
        .map(asMethod)
        .filter((m): m is PlanReminderPaymentMethod => m != null)
    : [];
  const summary =
    data.lastRunSummary && typeof data.lastRunSummary === "object"
      ? (data.lastRunSummary as PlanRemindersLastRunSummary)
      : null;

  return {
    daysBeforeRenewal:
      typeof data.daysBeforeRenewal === "number" ? data.daysBeforeRenewal : 2,
    remindOnExpiration:
      typeof data.remindOnExpiration === "boolean"
        ? data.remindOnExpiration
        : true,
    remindMidGrace:
      typeof data.remindMidGrace === "boolean" ? data.remindMidGrace : true,
    midGraceDay: typeof data.midGraceDay === "number" ? data.midGraceDay : 5,
    remindGraceEnd:
      typeof data.remindGraceEnd === "boolean" ? data.remindGraceEnd : true,
    atRiskDaysAfterGrace:
      typeof data.atRiskDaysAfterGrace === "number"
        ? data.atRiskDaysAfterGrace
        : 15,
    vesGraceDays: typeof data.vesGraceDays === "number" ? data.vesGraceDays : 10,
    includeTrialing:
      typeof data.includeTrialing === "boolean" ? data.includeTrialing : false,
    paymentMethods: methods.length ? methods : ["ves"],
    timeZone:
      typeof data.timeZone === "string" && data.timeZone
        ? data.timeZone
        : "America/Caracas",
    scheduleHour:
      typeof data.scheduleHour === "number" ? data.scheduleHour : 9,
    scheduleMinute:
      typeof data.scheduleMinute === "number" ? data.scheduleMinute : 0,
    schedulerEnabled: Boolean(data.schedulerEnabled),
    schedulerJobId:
      typeof data.schedulerJobId === "string" ? data.schedulerJobId : null,
    schedulerJobName:
      typeof data.schedulerJobName === "string" ? data.schedulerJobName : null,
    schedulerLocation:
      typeof data.schedulerLocation === "string"
        ? data.schedulerLocation
        : null,
    schedulerSchedule:
      typeof data.schedulerSchedule === "string"
        ? data.schedulerSchedule
        : null,
    schedulerTargetBaseUrl:
      typeof data.schedulerTargetBaseUrl === "string"
        ? data.schedulerTargetBaseUrl
        : null,
    lastRunAt:
      typeof data.lastRunAt === "string"
        ? data.lastRunAt
        : data.lastRunAt != null
        ? String(data.lastRunAt)
        : null,
    lastRunSummary: summary,
    updatedAt:
      typeof data.updatedAt === "string"
        ? data.updatedAt
        : data.updatedAt != null
        ? String(data.updatedAt)
        : null,
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : null,
  };
};

export const fetchPlanReminders = async (
  user: User
): Promise<PlanRemindersConfig> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/plan-reminders`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return mapConfig((await response.json()) as Record<string, unknown>);
};

export const updatePlanReminders = async (
  user: User,
  patch: Partial<{
    daysBeforeRenewal: number;
    remindOnExpiration: boolean;
    remindMidGrace: boolean;
    midGraceDay: number;
    remindGraceEnd: boolean;
    atRiskDaysAfterGrace: number;
    includeTrialing: boolean;
    paymentMethods: PlanReminderPaymentMethod[];
    timeZone: string;
    scheduleHour: number;
    scheduleMinute: number;
  }>
): Promise<PlanRemindersConfig> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/plan-reminders`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(patch),
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return mapConfig((await response.json()) as Record<string, unknown>);
};

export const upsertPlanRemindersSchedulerJob = async (
  user: User
): Promise<PlanRemindersConfig> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(
    `${baseUrl}/api/v1/platform/plan-reminders/scheduler-job`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ targetBaseUrl: baseUrl }),
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json()) as Record<string, unknown>;
  if (data.config && typeof data.config === "object") {
    return mapConfig(data.config as Record<string, unknown>);
  }
  return fetchPlanReminders(user);
};

export const deletePlanRemindersSchedulerJob = async (
  user: User
): Promise<PlanRemindersConfig> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/plan-reminders/scheduler-job`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json()) as Record<string, unknown>;
  if (data.config && typeof data.config === "object") {
    return mapConfig(data.config as Record<string, unknown>);
  }
  return fetchPlanReminders(user);
};

export const runPlanReminders = async (
  user: User,
  options?: { dryRun?: boolean }
): Promise<PlanRemindersRunResult> => {
  const token = await user.getIdToken();
  const dryRun = options?.dryRun === true;
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/plan-reminders/run?dry_run=${
      dryRun ? "true" : "false"
    }`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!response.ok) throw new Error(await parseError(response));
  return (await response.json()) as PlanRemindersRunResult;
};

export const fetchAtRiskOrganizations = async (
  user: User
): Promise<AtRiskOrganizationsResponse> => {
  const token = await user.getIdToken();
  const response = await fetch(
    `${getBackendBaseUrl()}/api/v1/platform/plan-reminders/at-risk`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(await parseError(response));
  const data = (await response.json()) as Record<string, unknown>;
  const orgsRaw = Array.isArray(data.organizations) ? data.organizations : [];
  const organizations: AtRiskOrganization[] = orgsRaw.map((row) => {
    const r = (row && typeof row === "object" ? row : {}) as Record<
      string,
      unknown
    >;
    return {
      id: String(r.id ?? ""),
      name: String(r.name ?? ""),
      email: typeof r.email === "string" ? r.email : null,
      planName: String(r.planName ?? ""),
      paymentMethod: String(r.paymentMethod ?? ""),
      subscriptionStatus: String(r.subscriptionStatus ?? ""),
      currentPeriodEnd: String(r.currentPeriodEnd ?? ""),
      graceEndedDaysAgo:
        typeof r.graceEndedDaysAgo === "number" ? r.graceEndedDaysAgo : 0,
      vesGraceDays: typeof r.vesGraceDays === "number" ? r.vesGraceDays : 10,
    };
  });
  const methods = Array.isArray(data.paymentMethods)
    ? data.paymentMethods
        .map(asMethod)
        .filter((m): m is PlanReminderPaymentMethod => m != null)
    : [];
  return {
    generatedAt: String(data.generatedAt ?? ""),
    timeZone: String(data.timeZone ?? "America/Caracas"),
    vesGraceDays:
      typeof data.vesGraceDays === "number" ? data.vesGraceDays : 10,
    atRiskDaysAfterGrace:
      typeof data.atRiskDaysAfterGrace === "number"
        ? data.atRiskDaysAfterGrace
        : 15,
    paymentMethods: methods.length ? methods : ["ves"],
    count: typeof data.count === "number" ? data.count : organizations.length,
    organizations,
  };
};
